import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useReaders } from '../hooks/useUsersState';
import { useUsersActions } from '../hooks/useUsersActions';
import { invitesApi } from '../../invites/invitesApi';
import UserModal from '../components/UserModal';
import { ListTable, SubPage, Button } from '../../../ui';
import Loading from '../../../ui/Loading';
import ConfirmDelete from '../../../ui/Modal/ConfirmDelete';
import PageHeader from '../../../ui/Page/PageHeader';
import PageControls from '../../../ui/Page/PageControls';

const UsersList = ({ modal }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortMode, setSortMode] = useState('asc');
  const [userToDelete, setUserToDelete] = useState(null);
  const [savedCurrentPage, setSavedCurrentPage] = useState(0);
  const length = 1000
  const navigate = useNavigate();

  const { readers: users = [], isLoading } = useReaders({
    pageLength: length,
    searchText: searchQuery,
    sortBy,
    sortMode
  });

  const { removeUserFromStore } = useUsersActions();

  const openUser = useCallback((obj) => {
    navigate(`/users/all/${obj.id}`)
  }, [navigate]);

  const handleSearch = useCallback((value) => {
    setSearchQuery(value)
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, [setSearchQuery]);

  const handleSort = useCallback((val) => {
    setSortBy(val);
    if (sortBy === val) {
      setSortMode('desc')
    }
    if (sortMode === 'desc') {
      setSortMode('asc')
    }
  }, [setSortBy, sortBy, setSortMode, sortMode]);

  const openDeleteConfirmation = (item) => {
    setUserToDelete(item)
  }

  const handleDeleteUser = async () => {
    await removeUserFromStore({ userId: userToDelete.id });
    setUserToDelete(null);
  }

  const tableInfo = [
    { label: t('globals.name'), handleClick: () => handleSort('name'), data: (item) => item.name },
    { label: t('globals.email'), handleClick: () => handleSort('email'), data: (item) => item.email },
    { label: t('globals.groups'), data: (item) => item.circles?.length },
    { label: t('globals.courses'), data: (item) => item.courses?.length },
    {
      label: '', menuOptions: () => [
        { label: t('globals.userInfo'), onClick: openUser },
        { label: t('globals.deleteUser'), onClick: (item) => openDeleteConfirmation(item) },
      ]
    },
  ]

  const openInviteDialog = useCallback(() => {
    dispatch(invitesApi.util.updateQueryData('invitesModalState', undefined, (draft) => {
      if (draft) {
        draft.isInviteModalOpen = true;
        draft.modalProps = { callbackFunc: 'UsersByGroupId' };
      }
    }));
  }, [dispatch]);

  return (
    <SubPage staticElements={
      <PageHeader>
        <PageControls
          onClearSearch={clearSearch}
          onSearch={handleSearch}
          placeholder={t('course.searchUser')}
          disableClear={!searchQuery}
          openModelButton={
            <Button icon='Invite' onClick={openInviteDialog}>{t('invites.inviteUser')}</Button>
          }
        />
      </PageHeader>
    }>
      {isLoading ? <Loading /> :
        <ListTable
          tableInfo={tableInfo}
          data={users}
          onRowClick={openUser}
          tableName={'users'}
          keepTrackOfPage={setSavedCurrentPage}
          defaultStartPage={savedCurrentPage}
        />}
      <UserModal open={!!modal} />
      <ConfirmDelete
        onClose={() => setUserToDelete(null)}
        onAcceptDelete={handleDeleteUser}
        open={!!userToDelete}
        description={t('users.removeTotallyFromAccount', { userName: userToDelete?.name })}
      />
    </SubPage>
  );
};

export default UsersList;
